import type { Bounds } from '~types';
import { padBounds } from '../geometry';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type FullViewBox = { vx: number; vy: number; vw: number; vh: number };

/* -------------------------------------------------------------------------- */
/*                              Utility functions                             */
/* -------------------------------------------------------------------------- */

/**
 * Create an SVG viewBox string from bounds.
 * Consumers should pre-pad the bounds if padding is needed (see `padBounds`).
 * @param bounds - The bounding box to represent as a viewBox
 * @returns An SVG viewBox attribute string ("minX minY width height")
 */
const boundsToViewBox = (bounds: Bounds): string => {
  return `${bounds.minLon} ${bounds.minLat} ${bounds.width} ${bounds.height}`;
};

/**
 * Compute a padded SVG viewBox string from bounds.
 * Normalizes coordinates relative to the bounds origin (minLon, maxLat)
 * so the viewBox aligns with the coordinate transformer's SVG space.
 */
const computeViewBox = (bounds: Bounds, padding: number): string => {
  const padded = padBounds(bounds, padding);
  const x = padded.minLon - bounds.minLon;
  const y = bounds.maxLat - padded.maxLat;
  return `${x} ${y} ${padded.width} ${padded.height}`;
};

/**
 * Computes the expanded viewBox that covers the full SVG element viewport,
 * including the padding areas introduced by `preserveAspectRatio="xMidYMid meet"`
 * (letterbox/pillarbox) when the container aspect ratio differs from the viewBox.
 *
 * @param viewBox - The SVG viewBox attribute string ("minX minY width height")
 * @param svgW - The SVG element's rendered width in pixels (e.g. `clientWidth`)
 * @param svgH - The SVG element's rendered height in pixels (e.g. `clientHeight`)
 * @returns The expanded viewBox in local coordinate units, or `null` if the input is invalid
 */
const computeFullViewBox = (viewBox: string, svgW: number, svgH: number): FullViewBox | null => {
  const parts = viewBox
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (parts.length !== 4 || parts.some(Number.isNaN)) return null;

  const [vx, vy, vw, vh] = parts;
  if (vw <= 0 || vh <= 0) return null;

  const scale = svgW > 0 && svgH > 0 ? Math.min(svgW / vw, svgH / vh) : 0;
  const padX = scale > 0 ? (svgW / scale - vw) / 2 : 0;
  const padY = scale > 0 ? (svgH / scale - vh) / 2 : 0;

  return { vx: vx - padX, vy: vy - padY, vw: vw + 2 * padX, vh: vh + 2 * padY };
};

/* -------------------------------------------------------------------------- */
/*                                   Export                                   */
/* -------------------------------------------------------------------------- */

export type { FullViewBox };
export { boundsToViewBox, computeFullViewBox, computeViewBox };
